import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import { EventAvailable as EventAvailableIcon, CreditCard as PayIcon, CalendarToday as CalendarIcon } from '@mui/icons-material';
import CustomerPageHeader from '../../components/customer/CustomerPageHeader';
import CustomerPageLoading from '../../components/customer/CustomerPageLoading';
import CustomerPageSection from '../../components/customer/CustomerPageSection';
import CustomerPrimaryButton from '../../components/customer/CustomerPrimaryButton';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import StripeEmbeddedCheckoutDialog from '../../components/common/StripeEmbeddedCheckoutDialog';
import AppointmentCancellationDetails from '../../components/customer/AppointmentCancellationDetails';
import AppointmentPaymentTermsDialog from '../../components/customer/AppointmentPaymentTermsDialog';
import {
  CUSTOMER_COLORS,
  customerAccentChipSx,
  customerCardPaperSx,
  customerServiceItemSx,
} from '../../constants/customerTheme';
import { BRAND, brandAlpha } from '../../theme/brandTokens';
import { normalizeLanguage, DEFAULT_LANGUAGE, getLocalizedValue } from '../../constants/Languages';
import api from '../../services/api';
import {
  getMyAppointments,
  createAppointmentCheckoutSession,
  getAppointmentCheckoutOutcome,
  abandonAppointmentCheckout,
  getAppointmentCheckoutTerms,
  getAppointmentCancellationPreview,
  cancelMyAppointment,
} from '../../services/appointmentService';
import { buildCustomerRoute } from '../../utils/storeUtils';
import { canCustomerCancel, canPayOnlineNow, isPayingTheBalance } from '../../utils/customerAppointmentActions';
import useCustomerOnlinePayments from '../../hooks/useCustomerOnlinePayments';
import requestDeduplicator from '../../utils/requestDeduplicator';
import { getServiceName } from '../../utils/serviceTranslations';
import { groupAppointmentServicesByProfessional } from '../../utils/appointmentServiceGroups';
import { formatCurrency } from '../../utils/currencyUtils';
import { useStore } from '../../context/StoreContext';
import { allowsOnline, normalizeCustomerPaymentMode } from '../../constants/CustomerPaymentMode';
import { getAppointmentStatusKey, getPaymentStatusKey } from '../../utils/appointmentStatusUtils';
import { isApiErrorCode } from '../../utils/apiErrorCode';
import useConsentGate from '../../hooks/useConsentGate';
import ConsentFormService, { ConsentContextType } from '../../services/consentFormService';

const PAYMENT_OUTCOME_FEEDBACK = {
  Paid: { type: 'success', key: 'customer.appointments.payment.success' },
  Processing: { type: 'info', key: 'customer.appointments.payment.processing' },
  Failed: { type: 'error', key: 'customer.appointments.payment.failed' },
  Expired: { type: 'warning', key: 'customer.appointments.payment.expired' },
  Unknown: { type: 'info', key: 'customer.appointments.payment.processing' },
};

// Semantic status colours are literal hexes (alpha() maths is fine); the brand-coloured
// awaiting-payment status goes through brand tokens so a store's palette applies.
const statusTone = (color) => ({ color, bg: alpha(color, 0.14), border: alpha(color, 0.25) });

const statusConfig = {
  pending: { key: 'pending', ...statusTone('#ed6c02') },
  cancelled: { key: 'cancelled', ...statusTone('#d32f2f') },
  confirmed: { key: 'confirmed', ...statusTone('#0288d1') },
  completed: { key: 'completed', ...statusTone('#2e7d32') },
  noShow: { key: 'noShow', ...statusTone('#e11d48') },
  awaitingPayment: {
    key: 'awaitingPayment',
    color: BRAND.accentText,
    bg: brandAlpha('accent', 0.14),
    border: brandAlpha('accent', 0.25),
  },
};

function getStatusKey(status) {
  const key = getAppointmentStatusKey(status);
  return key === 'unknown' ? null : key;
}

function getStatusData(status, t, awaitingConfirmation = false) {
  if (awaitingConfirmation) {
    return {
      label: t('customer.appointments.status.awaitingConfirmation'),
      ...statusTone('#ed6c02'),
    };
  }

  const statusKey = getStatusKey(status);
  const cfg = statusKey ? statusConfig[statusKey] : null;
  if (cfg) {
    return {
      label: t(`customer.appointments.status.${cfg.key}`),
      color: cfg.color,
      bg: cfg.bg,
      border: cfg.border,
    };
  }

  return { label: t('customer.appointments.status.unknown'), ...statusTone('#6b7280') };
}

function formatDateLabel(rawDate, locale) {
  if (!rawDate) return '—';

  const date = new Date(`${rawDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) return rawDate;

  return new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  }).format(date);
}

function normalizeTime(rawTime) {
  if (!rawTime || typeof rawTime !== 'string') return '—';
  const parts = rawTime.split(':');
  if (parts.length < 2) return rawTime;
  return `${parts[0]}:${parts[1]}`;
}

function formatPeriod(period) {
  if (!period?.from || !period?.to) return '';
  return `${normalizeTime(period.from)} - ${normalizeTime(period.to)}`;
}

export default function MyAppointmentsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { i18n, t } = useTranslation();
  const { storeId, storeOverview } = useStore();
  // Also false for a customer the store takes payments from in store only.
  const customerOnlinePaymentsAllowed = useCustomerOnlinePayments(storeId);
  const storeTakesOnlinePayment = customerOnlinePaymentsAllowed
    && allowsOnline(normalizeCustomerPaymentMode(storeOverview?.appointmentCustomerPaymentMode));
  const languageCode = normalizeLanguage(i18n.language) || DEFAULT_LANGUAGE;
  const storeLanguage = normalizeLanguage(storeOverview?.storeLanguage) || DEFAULT_LANGUAGE;
  const currencyCode = storeOverview?.currency || 'EUR';

  const [appointments, setAppointments] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checkoutAppointmentId, setCheckoutAppointmentId] = useState(null);
  const checkoutCompletedRef = useRef(false);
  const checkoutSessionRef = useRef(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelPreview, setCancelPreview] = useState(null);
  const [cancelPreviewLoading, setCancelPreviewLoading] = useState(false);
  const [cancelPreviewFailed, setCancelPreviewFailed] = useState(false);
  const [payTermsTarget, setPayTermsTarget] = useState(null);
  const [payTerms, setPayTerms] = useState(null);
  const [payTermsLoading, setPayTermsLoading] = useState(false);
  const [payTermsFailed, setPayTermsFailed] = useState(false);
  const [checkoutAcceptedTerms, setCheckoutAcceptedTerms] = useState(false);
  const [paymentFeedback, setPaymentFeedback] = useState(null);
  const highlightedAppointmentId = searchParams.get('appointment');
  const highlightedCardRef = useRef(null);
  const { reviewForms, consentDialog } = useConsentGate(storeId);
  const [pendingConsentsByAppointmentId, setPendingConsentsByAppointmentId] = useState({});

  // Forms still to accept for bookings the store made for this customer.
  const loadPendingConsents = useCallback(async () => {
    if (!storeId) {
      return;
    }

    try {
      const items = await ConsentFormService.getMyPendingConsents(storeId);
      setPendingConsentsByAppointmentId(Object.fromEntries(items
        .filter((item) => item.contextType === ConsentContextType.Booking && item.appointmentId)
        .map((item) => [item.appointmentId, item.forms])));
    } catch (consentError) {
      console.error('Failed to load pending consent forms:', consentError);
      setPendingConsentsByAppointmentId({});
    }
  }, [storeId]);

  useEffect(() => {
    loadPendingConsents();
  }, [loadPendingConsents]);

  const handleReviewConsents = async (appointmentId) => {
    const result = await reviewForms(pendingConsentsByAppointmentId[appointmentId], {
      contextType: ConsentContextType.PendingRequest,
      appointmentId,
    });
    if (result.accepted) {
      setPaymentFeedback({ type: 'success', key: 'consentForms.customerPage.accepted' });
      await loadPendingConsents();
    }
  };

  useEffect(() => {
    if (!highlightedAppointmentId || loading) {
      return;
    }

    highlightedCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlightedAppointmentId, loading, appointments]);

  const loadAppointments = useCallback(() => {
    if (!storeId) return () => {};

    let cancelled = false;

    setLoading(true);
    setError(null);

    const appointmentsRequestKey = `customer-my-appointments-${storeId}`;
    const teamMembersRequestKey = `customer-my-appointments-team-members-${storeId}`;

    Promise.all([
      requestDeduplicator.deduplicate(appointmentsRequestKey, () => getMyAppointments(storeId)),
      requestDeduplicator.deduplicate(teamMembersRequestKey, () =>
        api.get(`/team-members?storeId=${storeId}&activeOnly=true`).then((response) => response.data)
      ).catch(() => []),
    ])
      .then(([appointmentsData, teamMembersData]) => {
        if (!cancelled) {
          setAppointments(Array.isArray(appointmentsData) ? appointmentsData : []);
          setTeamMembers(Array.isArray(teamMembersData) ? teamMembersData : []);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.response?.data?.message || i18n.t('customer.appointments.myAppointments.loadError'));
          setAppointments([]);
          setTeamMembers([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [storeId, i18n]);

  useEffect(() => {
    return loadAppointments();
  }, [loadAppointments]);

  useEffect(() => {
    if (!location.state?.paymentProcessing) return;
    setPaymentFeedback(PAYMENT_OUTCOME_FEEDBACK.Processing);
    navigate(location.pathname + location.search, { replace: true, state: null });
  }, [location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    // Present when a customer returns from an off-site payment step (e.g. Klarna or
    // Scalapay approval) via the checkout session's ReturnUrl. Stripe sends them back here
    // whether the payment succeeded or failed and the URL says nothing about which, so ask
    // the server, which reads the outcome from Stripe itself.
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      return undefined;
    }

    let cancelled = false;

    // Read once: left in the URL, every reload would ask again and show the banner again.
    const forgetSessionId = () => {
      const next = new URLSearchParams(searchParams);
      next.delete('session_id');
      setSearchParams(next, { replace: true });
    };

    getAppointmentCheckoutOutcome(sessionId)
      .then(({ outcome, bookingReleased }) => {
        if (cancelled) {
          return;
        }

        if (bookingReleased) {
          setPaymentFeedback({ type: 'error', key: 'customer.appointments.payment.failedNotBooked' });
        } else {
          setPaymentFeedback(PAYMENT_OUTCOME_FEEDBACK[outcome] ?? PAYMENT_OUTCOME_FEEDBACK.Unknown);
        }

        // A settled or released payment means the booking just changed server-side.
        if (outcome === 'Paid' || bookingReleased) {
          loadAppointments();
        }
        forgetSessionId();
      })
      .catch((err) => {
        console.error('Failed to resolve checkout outcome:', err);
        if (!cancelled) {
          setPaymentFeedback(PAYMENT_OUTCOME_FEEDBACK.Unknown);
          forgetSessionId();
        }
      });

    return () => {
      cancelled = true;
    };
  }, [loadAppointments, searchParams, setSearchParams]);

  const sortedAppointments = useMemo(() => {
    return [...appointments].sort((a, b) => {
      const dateA = new Date(`${a?.date || ''}T${a?.period?.from || '00:00:00'}`);
      const dateB = new Date(`${b?.date || ''}T${b?.period?.from || '00:00:00'}`);
      return dateB - dateA;
    });
  }, [appointments]);

  useEffect(() => {
    const appointmentId = cancelTarget?.id;
    setCancelPreview(null);
    setCancelPreviewFailed(false);
    if (!appointmentId) {
      return undefined;
    }

    let cancelled = false;
    setCancelPreviewLoading(true);
    getAppointmentCancellationPreview(appointmentId)
      .then((preview) => {
        if (!cancelled) setCancelPreview(preview);
      })
      .catch(() => {
        if (!cancelled) setCancelPreviewFailed(true);
      })
      .finally(() => {
        if (!cancelled) setCancelPreviewLoading(false);
      });

    return () => { cancelled = true; };
  }, [cancelTarget?.id]);

  // Terms not accepted while booking (on-request or waitlist bookings) are shown before paying.
  const handlePayNow = async (appointmentId) => {
    checkoutCompletedRef.current = false;
    checkoutSessionRef.current = null;
    setPayTermsTarget(appointmentId);
    setPayTerms(null);
    setPayTermsFailed(false);
    setPayTermsLoading(true);
    try {
      const terms = await getAppointmentCheckoutTerms(appointmentId, languageCode);
      if (terms.alreadyAccepted) {
        setPayTermsTarget(null);
        setCheckoutAcceptedTerms(false);
        setCheckoutAppointmentId(appointmentId);
        return;
      }
      setPayTerms(terms);
    } catch (termsError) {
      console.error('Failed to load payment terms:', termsError);
      setPayTermsFailed(true);
    } finally {
      setPayTermsLoading(false);
    }
  };

  const handleAcceptPayTerms = () => {
    checkoutCompletedRef.current = false;
    checkoutSessionRef.current = null;
    setCheckoutAcceptedTerms(true);
    setCheckoutAppointmentId(payTermsTarget);
    setPayTermsTarget(null);
  };

  const applyCheckoutOutcome = ({ outcome, bookingReleased }) => {
    if (bookingReleased) {
      setPaymentFeedback({ type: 'error', key: 'customer.appointments.payment.failedNotBooked' });
    } else {
      setPaymentFeedback(PAYMENT_OUTCOME_FEEDBACK[outcome] ?? PAYMENT_OUTCOME_FEEDBACK.Unknown);
    }
    loadAppointments();
  };

  const handleCheckoutComplete = async () => {
    checkoutCompletedRef.current = true;
    const sessionId = checkoutSessionRef.current?.sessionId;
    setCheckoutAppointmentId(null);
    if (!sessionId) {
      setPaymentFeedback({ type: 'success', key: 'customer.appointments.payment.success' });
      loadAppointments();
      return;
    }

    try {
      applyCheckoutOutcome(await getAppointmentCheckoutOutcome(sessionId));
    } catch (outcomeError) {
      console.error('Failed to confirm checkout outcome:', outcomeError);
      setPaymentFeedback(PAYMENT_OUTCOME_FEEDBACK.Unknown);
      loadAppointments();
    }
  };

  const handleCheckoutClose = () => {
    if (checkoutCompletedRef.current) {
      checkoutCompletedRef.current = false;
      setCheckoutAppointmentId(null);
      return;
    }

    const appointmentId = checkoutAppointmentId;
    setCheckoutAppointmentId(null);
    if (!appointmentId) return;

    abandonAppointmentCheckout(appointmentId)
      .then((result) => {
        if (result.outcome === 'Released') {
          setPaymentFeedback({ type: 'error', key: 'customer.appointments.payment.failedNotBooked' });
          loadAppointments();
        } else if (result.outcome === 'Processing') {
          setPaymentFeedback(PAYMENT_OUTCOME_FEEDBACK.Processing);
        } else if (result.outcome === 'Confirmed') {
          loadAppointments();
        }
      })
      .catch((abandonError) => {
        console.error('Failed to release unpaid booking:', abandonError);
      });
  };

  const handleConfirmCancel = async () => {
    const appointmentId = cancelTarget?.id;
    if (!appointmentId) return;

    try {
      setCancelling(true);
      const result = await cancelMyAppointment(appointmentId);
      setAppointments((prev) =>
        prev.map((appointment) =>
          appointment.id === appointmentId
            ? { ...appointment, status: 'Cancelled' }
            : appointment
        )
      );
      if (result?.refundIssued && result.refundAmount > 0) {
        setPaymentFeedback({
          type: 'success',
          key: 'customer.appointments.myAppointments.refund.successWithRefund',
          params: { amount: formatCurrency(result.refundAmount, result.currency || currencyCode) },
        });
      } else if (result?.refundPending) {
        setPaymentFeedback({ type: 'info', key: 'customer.appointments.myAppointments.refund.successPending' });
      } else {
        setPaymentFeedback({ type: 'success', key: 'customer.appointments.myAppointments.cancelSuccess' });
      }
      setCancelTarget(null);
      loadAppointments();
    } catch (err) {
      console.error('Cancel appointment error:', err);
      if (isApiErrorCode(err, 'CustomerAppointmentCancelTooLate')) {
        setPaymentFeedback({ type: 'error', key: 'customer.appointments.myAppointments.cancelTooLate' });
      } else {
        setPaymentFeedback({ type: 'error', key: 'customer.appointments.myAppointments.cancelError' });
      }
    } finally {
      setCancelling(false);
    }
  };

  const teamMemberNameById = useMemo(() => {
    return new Map((teamMembers || []).map((teamMember) => [String(teamMember.id).toLowerCase(), teamMember.name]));
  }, [teamMembers]);

  const goToBooking = () => navigate(buildCustomerRoute(storeId, 'booking'));

  return (
    <Box>
      <CustomerPageHeader
        icon={CalendarIcon}
        title={t('customer.appointments.myAppointments.title')}
        subtitle={t('customer.appointments.myAppointments.subtitle')}
        action={(
          <CustomerPrimaryButton
            onClick={goToBooking}
            startIcon={<EventAvailableIcon sx={{ fontSize: 20 }} />}
          >
            {t('customer.appointments.bookNow')}
          </CustomerPrimaryButton>
        )}
      />

      {paymentFeedback && (
        <Alert severity={paymentFeedback.type} sx={{ mb: 2 }} onClose={() => setPaymentFeedback(null)}>
          {t(paymentFeedback.key, paymentFeedback.params)}
        </Alert>
      )}

      {loading && <CustomerPageLoading />}

      {!loading && error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && sortedAppointments.length === 0 && (
        <CustomerPageSection variant="empty">
          <Typography variant="body1" sx={{ fontWeight: 600, color: CUSTOMER_COLORS.text }}>
            {t('customer.appointments.myAppointments.emptyTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
            {t('customer.appointments.myAppointments.emptySubtitle')}
          </Typography>
          <CustomerPrimaryButton
            onClick={goToBooking}
            startIcon={<EventAvailableIcon sx={{ fontSize: 20 }} />}
          >
            {t('customer.appointments.bookNow')}
          </CustomerPrimaryButton>
        </CustomerPageSection>
      )}

      {!loading && !error && sortedAppointments.length > 0 && (
        <Stack spacing={2}>
          {sortedAppointments.map((appointment, index) => {
            const status = getStatusData(appointment?.status, t, appointment?.awaitingConfirmation);
            const services = Array.isArray(appointment?.services) ? appointment.services : [];
            const serviceGroups = groupAppointmentServicesByProfessional(services, (service) =>
              getServiceName(service?.name, languageCode, storeLanguage)
            );
            const from = normalizeTime(appointment?.period?.from);
            const to = normalizeTime(appointment?.period?.to);
            const appointmentId = String(appointment?.id || '').toLowerCase();
            const isHighlighted = Boolean(
              highlightedAppointmentId
              && appointmentId
              && appointmentId === highlightedAppointmentId.toLowerCase()
            );
            const coveredByMembership = Boolean(appointment?.coveredByMembership);
            const coveredByServicePackage = Boolean(appointment?.coveredByServicePackage);
            const servicePackageLabel = getLocalizedValue(
              appointment?.servicePackageName,
              languageCode,
              storeLanguage,
              ''
            );
            const pendingMembershipEnrollment = Boolean(appointment?.pendingMembershipEnrollment);
            const amountDue = Number(
              appointment?.amountDue
              ?? appointment?.totalPrice
              ?? 0
            );
            const additionalCostAmount = Number(appointment?.additionalCostAmount || 0);
            const paymentKey = getPaymentStatusKey(appointment?.paymentStatus);
            const onlinePrepaidAmount = Number(appointment?.onlinePrepaidAmount || 0);
            const membershipPlanLabel = getLocalizedValue(
              appointment?.membershipPlanName,
              languageCode,
              storeLanguage,
              ''
            );

            return (
              <Paper
                key={appointment?.id || `${appointment?.date || 'date'}-${appointment?.period?.from || 'time'}-${index}`}
                ref={isHighlighted ? highlightedCardRef : null}
                sx={{
                  ...customerCardPaperSx,
                  border: `1px solid ${isHighlighted ? brandAlpha('accent', 0.55) : status.border}`,
                  boxShadow: isHighlighted ? `0 0 0 2px ${brandAlpha('accent', 0.25)}` : customerCardPaperSx.boxShadow,
                }}
              >
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: CUSTOMER_COLORS.text, mb: 0.5 }}>
                      {formatDateLabel(appointment?.date, languageCode)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {from} - {to}
                    </Typography>
                  </Box>

                  <Stack
                    direction="row"
                    spacing={1}
                    useFlexGap
                    flexWrap="wrap"
                    sx={{ alignSelf: { xs: 'flex-start', sm: 'center' }, justifyContent: { sm: 'flex-end' } }}
                  >
                    {coveredByMembership && (
                      <Chip
                        label={t('customer.appointments.myAppointments.coveredByMembership')}
                        sx={{
                          bgcolor: brandAlpha('accent', 0.14),
                          color: BRAND.accentText,
                          fontWeight: 700,
                        }}
                      />
                    )}
                    {coveredByServicePackage && (
                      <Chip
                        label={t('customer.appointments.myAppointments.coveredByPackage')}
                        sx={{
                          bgcolor: brandAlpha('accent', 0.14),
                          color: BRAND.accentText,
                          fontWeight: 700,
                        }}
                      />
                    )}
                    {pendingMembershipEnrollment && (
                      <Chip
                        label={t('customer.appointments.myAppointments.pendingMembershipEnrollment')}
                        sx={{
                          bgcolor: brandAlpha('accentDark', 0.12),
                          color: BRAND.accentDark,
                          fontWeight: 700,
                        }}
                      />
                    )}
                    <Chip
                      label={status.label}
                      sx={{
                        bgcolor: status.bg,
                        color: status.color,
                        fontWeight: 700,
                      }}
                    />
                  </Stack>
                </Stack>

                {appointment?.awaitingConfirmation && (
                  <Alert severity="info" sx={{ mt: 1.5, borderRadius: 1.5 }}>
                    {t('customer.appointments.myAppointments.awaitingConfirmationNote')}
                  </Alert>
                )}

                {pendingConsentsByAppointmentId[appointment?.id]?.length ? (
                  <Alert
                    id={`appointment-consent-pending-${appointment.id}`}
                    severity="warning"
                    sx={{ mt: 1.5, borderRadius: 1.5, alignItems: 'center' }}
                    action={(
                      <CustomerPrimaryButton
                        size="small"
                        id={`appointment-consent-review-${appointment.id}`}
                        onClick={() => handleReviewConsents(appointment.id)}
                      >
                        {t('consentForms.customerPage.review')}
                      </CustomerPrimaryButton>
                    )}
                  >
                    {t('consentForms.staff.pending', { count: pendingConsentsByAppointmentId[appointment.id].length })}
                  </Alert>
                ) : null}

                <Box sx={{ mt: 1.25 }}>
                  {amountDue > 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {t('customer.appointments.myAppointments.amountDue')}:{' '}
                      <Box component="span" sx={{ color: CUSTOMER_COLORS.text, fontWeight: 700 }}>
                        {formatCurrency(amountDue, currencyCode)}
                      </Box>
                    </Typography>
                  )}
                  {additionalCostAmount > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                      {t('customer.appointments.myAppointments.additionalCost')}: {formatCurrency(additionalCostAmount, currencyCode)}
                    </Typography>
                  )}
                  {paymentKey === 'depositPaid' && onlinePrepaidAmount > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                      {t('customer.appointments.myAppointments.depositPaidNote', { amount: formatCurrency(onlinePrepaidAmount, currencyCode) })}
                    </Typography>
                  )}
                  {(paymentKey === 'refunded' || paymentKey === 'partiallyRefunded') && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                      {t(paymentKey === 'refunded'
                        ? 'customer.appointments.myAppointments.refundedNote'
                        : 'customer.appointments.myAppointments.partiallyRefundedNote')}
                    </Typography>
                  )}
                  {membershipPlanLabel && (coveredByMembership || pendingMembershipEnrollment) && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                      {pendingMembershipEnrollment
                        ? t('customer.appointments.myAppointments.membershipPriceNote', { plan: membershipPlanLabel })
                        : t('customer.appointments.myAppointments.membershipPlanNote', { plan: membershipPlanLabel })}
                    </Typography>
                  )}
                  {servicePackageLabel && coveredByServicePackage && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                      {t('customer.appointments.myAppointments.packageNote', { package: servicePackageLabel })}
                    </Typography>
                  )}
                </Box>

                <Divider sx={{ my: 1.5 }} />

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('customer.appointments.myAppointments.services')}
                </Typography>

                <Stack spacing={1.25}>
                  {serviceGroups.map((group) => {
                    const professional = teamMemberNameById.get(group.teamMemberId)
                      || ((appointment?.openToAnyProfessional || appointment?.awaitingConfirmation) && !group.teamMemberId
                        ? t('customer.appointments.booking.anyProfessional')
                        : t('customer.appointments.myAppointments.unnamedProfessional'));
                    const servicePeriod = formatPeriod(group.period);

                    return (
                      <Box
                        key={`${appointment?.id}-${group.teamMemberId}`}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          gap: 0.5,
                          ...customerServiceItemSx,
                        }}
                      >
                        <Chip
                          label={group.names.join(' + ')}
                          variant="outlined"
                          sx={{
                            ...customerAccentChipSx,
                            height: 'auto',
                            maxWidth: '100%',
                            '& .MuiChip-label': {
                              display: 'block',
                              whiteSpace: 'normal',
                              py: 0.5,
                            },
                          }}
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
                          {t('customer.appointments.myAppointments.professional')}: {professional}
                        </Typography>
                        {servicePeriod ? (
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                            {servicePeriod}
                          </Typography>
                        ) : null}
                      </Box>
                    );
                  })}
                </Stack>

                {((storeTakesOnlinePayment &&
                  canPayOnlineNow(appointment))
                  || canCustomerCancel(appointment)) && (
                  <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
                    {canCustomerCancel(appointment) && (
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={cancelling || checkoutAppointmentId !== null}
                        onClick={() => setCancelTarget(appointment)}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          color: '#c62828',
                          borderColor: '#f5c2c0',
                          background: '#fdecea',
                          '&:hover': {
                            borderColor: '#f5c2c0',
                            background: '#f9d4d2',
                          },
                        }}
                      >
                        {t('customer.appointments.myAppointments.cancel')}
                      </Button>
                    )}
                    {storeTakesOnlinePayment &&
                      canPayOnlineNow(appointment) && (
                      <CustomerPrimaryButton
                        size="small"
                        startIcon={<PayIcon />}
                        disabled={checkoutAppointmentId !== null || cancelling}
                        onClick={() => handlePayNow(appointment?.id)}
                      >
                        {t(isPayingTheBalance(appointment)
                          ? 'customer.appointments.myAppointments.payBalance'
                          : 'customer.appointments.myAppointments.payNow')}
                      </CustomerPrimaryButton>
                    )}
                  </Box>
                )}
              </Paper>
            );
          })}
        </Stack>
      )}

      <ConfirmDialog
        open={Boolean(cancelTarget)}
        onClose={() => {
          if (!cancelling) setCancelTarget(null);
        }}
        onConfirm={handleConfirmCancel}
        title={t('customer.appointments.myAppointments.cancelTitle')}
        confirmText={t('customer.appointments.myAppointments.cancelConfirm')}
        loading={cancelling}
        closeOnConfirm={false}
        // The server is the real gate, so a failed preview still lets the customer cancel.
        confirmDisabled={cancelPreviewLoading || cancelPreview?.canCancel === false}
      >
        <Typography variant="body1">{t('customer.appointments.myAppointments.cancelMessage')}</Typography>
        <AppointmentCancellationDetails
          preview={cancelPreview}
          loading={cancelPreviewLoading}
          failed={cancelPreviewFailed}
          currencyCode={currencyCode}
        />
      </ConfirmDialog>

      <AppointmentPaymentTermsDialog
        open={Boolean(payTermsTarget)}
        terms={payTerms}
        loading={payTermsLoading}
        failed={payTermsFailed}
        currencyCode={currencyCode}
        onClose={() => setPayTermsTarget(null)}
        onAccept={handleAcceptPayTerms}
      />

      {consentDialog}

      <StripeEmbeddedCheckoutDialog
        open={Boolean(checkoutAppointmentId)}
        onClose={handleCheckoutClose}
        createSession={async () => {
          const session = await createAppointmentCheckoutSession(checkoutAppointmentId, {
            acceptedCancellationPolicy: checkoutAcceptedTerms,
            language: languageCode,
          });
          checkoutSessionRef.current = session;
          return session;
        }}
        onComplete={handleCheckoutComplete}
      />
    </Box>
  );
}
